import { Skeleton } from "@/components/shared/Skeleton";
import { SlideThumbnailBody } from "@/components/slides/list/SlideThumbnail";
import { AppButton } from "@/components/teacher/AppButton";
import { AppDialog } from "@/components/teacher/AppDialog";
import { CheckCircle } from "@/components/teacher/CheckCircle";
import { type SelectedSlide, SlidePickerDialog } from "@/components/teacher/SlidePickerDialog";
import { StatPill } from "@/components/teacher/StatPill";
import {
  AssignHomeworkDocument,
  AssignableReachDocument,
  HomeworkEligibilityDocument,
  type HomeworkEligibilityQuery,
} from "@/generated/graphql";
import { type Dictionary, type TranslationKey, format, useT } from "@/i18n";
import { CANVAS_SIZE } from "@/lib/constants/slides";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { HiX } from "react-icons/hi";
import type { ClassroomScope } from "./classroomScope";
import { type RosterStudent, useClassroomRoster } from "./useClassroomData";

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f"];
const INPUT_CLASS =
  "px-3 py-2 bg-white text-app-text placeholder:text-app-dim border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-app-aqua";

type Eligibility = HomeworkEligibilityQuery["homeworkEligibility"][number];

/** Seeds the form, as reassigning an existing homework does. The due date always starts fresh. */
interface AssignHomeworkInitial {
  title: string;
  studentIds: string[];
  slides: SelectedSlide[];
}

interface AssignHomeworkModalProps {
  classroomId: string;
  scope?: ClassroomScope;
  initial?: AssignHomeworkInitial;
  onClose: () => void;
}

function defaultDueDate(): string {
  const due = new Date();
  due.setDate(due.getDate() + 7);
  return due.toISOString().slice(0, 10);
}

/** Keyed by main slide id, the same key the picker uses, so a seeded slide toggles off there. */
function toSelection(slides: SelectedSlide[] = []): Map<string, SelectedSlide> {
  const selection = new Map<string, SelectedSlide>();
  for (const slide of slides) {
    const mainSlideId = slide.slideIds[0];
    if (mainSlideId) selection.set(mainSlideId, slide);
  }
  return selection;
}

/**
 * The dot a row carries, in the order the legend lists them. Hollow rather than a fourth colour
 * for the unverified case: it is the same "may not be covered" as the filled one, unconfirmed.
 */
const ROW_DOTS = {
  blocked: {
    dot: "bg-app-overdue",
    text: "text-red-800",
    meaningKey: "assign.dot.blocked",
  },
  absent: {
    dot: "bg-gray-300",
    text: "text-gray-400",
    meaningKey: "assign.dot.absent",
  },
  unverified: {
    dot: "border-2 border-app-overdue",
    text: "",
    meaningKey: "assign.dot.unverified",
  },
} as const satisfies Record<string, { dot: string; text: string; meaningKey: TranslationKey }>;

type RowDot = keyof typeof ROW_DOTS;

/**
 * A row can be unpickable for three different reasons, and a greyed-out name on its own tells a
 * teacher none of them: no account here, an MBU ticket that misses a slide already picked, or a
 * check still in flight. `dot` colours the row and keys the legend; `hint` is the tooltip, which
 * is the only place the specific product or level cap appears.
 *
 * The ticket check is a backstop: slides are picked for the students chosen first, so only a
 * ticket that lapsed since then can still rule one out.
 */
function rowState(
  t: Dictionary,
  student: RosterStudent,
  verdict: Eligibility | undefined,
  hasSlides: boolean
): { disabled: boolean; hint: string; dot: RowDot | null } {
  if (!student.userId) {
    return { disabled: true, hint: t["assign.hint.notSignedIn"], dot: "absent" };
  }
  if (!hasSlides) return { disabled: false, hint: student.username, dot: null };
  if (!verdict) return { disabled: true, hint: t["assign.hint.checking"], dot: null };

  if (!verdict.eligible) {
    return {
      disabled: true,
      hint: format(t["assign.hint.notCovered"], { missing: verdict.missing.join(", ") }),
      dot: "blocked",
    };
  }

  return {
    disabled: false,
    hint: verdict.verified
      ? student.username
      : format(t["assign.hint.unverified"], { username: student.username }),
    dot: verdict.verified ? null : "unverified",
  };
}

/**
 * Colour carries the reason on a dotted row, so it keeps full contrast; only the states with
 * nothing to say yet — no slides picked, a check in flight — fade instead.
 */
function rowClassName(state: ReturnType<typeof rowState>): string {
  const interactive = state.disabled ? "" : "cursor-pointer hover:bg-app-sky-soft";
  if (state.dot) return `${interactive} ${ROW_DOTS[state.dot].text}`;
  return state.disabled ? "opacity-50" : interactive;
}

export function AssignHomeworkModal({
  classroomId,
  scope = "teacher",
  initial,
  onClose,
}: AssignHomeworkModalProps) {
  //record the title from, title clear up and change if it's from autofill
  type TitleSource = "automatic" | "custom";
  const [title, setTitle] = useState(initial?.title ?? "");
  const [titleSource, setTitleSource] = useState<TitleSource>(
    initial?.title.trim() ? "custom" : "automatic"
  );
  const updateSelectedSlides = (nextSelection: Map<string, SelectedSlide>) => {
    setSelectedSlides(nextSelection);
    if (titleSource === "custom") {
      return;
    }
    //clear slides title when it's autofill && no slides
    if (nextSelection.size === 0) {
      setTitle("");
      return;
    }
    //autofill title
    const firstSelectedSlide = [...nextSelection.values()][0];
    const selectedPath = firstSelectedSlide?.lessonPath ?? firstSelectedSlide?.lessonName ?? "";

    setTitle(selectedPath);
  };

  const [dueDate, setDueDate] = useState(defaultDueDate);
  const [studentIds, setStudentIds] = useState<Set<string>>(new Set(initial?.studentIds ?? []));
  const [selectedSlides, setSelectedSlides] = useState<Map<string, SelectedSlide>>(() =>
    toSelection(initial?.slides)
  );
  const [isPickingSlides, setIsPickingSlides] = useState(false);
  const t = useT();

  const [assignHomework, { loading: assigning }] = useMutation(AssignHomeworkDocument, {
    refetchQueries: ["StudentHomework"],
  });
  const {
    students,
    loading: rosterLoading,
    error: rosterError,
  } = useClassroomRoster(scope, classroomId);

  const slideIds = [...selectedSlides.values()].flatMap((slide) => slide.slideIds);
  const eligibilityQuery = useQuery(HomeworkEligibilityDocument, {
    variables: { classroomId, slideIds },
    skip: slideIds.length === 0,
  });
  const reachQuery = useQuery(AssignableReachDocument, {
    variables: { classroomId, studentIds: [...studentIds] },
    skip: studentIds.size === 0,
  });

  const verdicts = eligibilityQuery.data?.homeworkEligibility;
  const eligibility = new Map((verdicts ?? []).map((row) => [row.userId, row]));

  // Picking another slide can rule out a student already ticked; drop them rather than let the
  // assign fail on the server's own check.
  useEffect(() => {
    setStudentIds((current) => {
      const next = new Set(
        [...current].filter(
          (userId) => (verdicts ?? []).find((row) => row.userId === userId)?.eligible !== false
        )
      );
      return next.size === current.size ? current : next;
    });
  }, [verdicts]);

  const rows = students.map((student) => ({
    student,
    state: rowState(t, student, eligibility.get(student.userId ?? ""), slideIds.length > 0),
  }));
  const selectable = rows.filter((row) => !row.state.disabled);
  // Only the dots actually on screen, so the legend never explains a state nobody is in.
  const legend = (Object.keys(ROW_DOTS) as RowDot[]).filter((dot) =>
    rows.some((row) => row.state.dot === dot)
  );

  const slideCount = slideIds.length;
  const lessonCount = new Set(
    [...selectedSlides.values()].map((slide) => slide.lessonName).filter(Boolean)
  ).size;

  const toggleStudent = (userId: string) => {
    const next = new Set(studentIds);
    if (!next.delete(userId)) next.add(userId);
    setStudentIds(next);
  };

  const handleSubmit = async () => {
    try {
      await assignHomework({
        variables: {
          classroomId,
          title: title.trim(),
          dueAt: new Date(dueDate),
          studentIds: [...studentIds],
          slideIds,
        },
      });
      toast.success(format(t["assign.assigned"], { count: studentIds.size }));
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t["assign.failed"]);
    }
  };

  const canSubmit =
    title.trim().length > 0 && studentIds.size > 0 && selectedSlides.size > 0 && !assigning;

  const lessonPath = [...selectedSlides.values()][0]?.lessonPath;

  return (
    <AppDialog
      label={t["assign.title"]}
      size="lg"
      measure="max-w-3xl"
      onDismiss={onClose}
      title={<span className="app-title min-w-0 flex-1 truncate">{t["assign.title"]}</span>}
      actions={
        <>
          <AppButton tone="quiet" size="sm" onClick={onClose}>
            {t["common.cancel"]}
          </AppButton>
          <AppButton
            size="sm"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            isLoading={assigning}
          >
            {t["assign.submit"]}
          </AppButton>
        </>
      }
    >
      <div className="app-card grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5">
        <label className="flex flex-col gap-1">
          <span className="app-label">{t["assign.titleLabel"]}</span>
          <input
            type="text"
            value={title}
            onChange={(event) => {
              const nextTitle = event.target.value;
              setTitle(nextTitle);
              setTitleSource(nextTitle.trim() ? "custom" : "automatic");
            }}
            placeholder={lessonPath ?? t["assign.titlePlaceholder"]}
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="app-label">{t["assign.dueLabel"]}</span>
          <input
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            className={INPUT_CLASS}
          />
        </label>
      </div>

      {/* Students come first: which slides may be picked depends on what they can open. */}

      <section className="app-card space-y-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
          <span className="app-title">{t["assign.students"]}</span>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <StatPill tone={studentIds.size > 0 ? "aqua" : "muted"}>
              {format(t["assign.selectedCount"], { count: studentIds.size })}
            </StatPill>
            <AppButton
              tone="quiet"
              size="sm"
              disabled={selectable.length === 0}
              onClick={() =>
                setStudentIds(
                  studentIds.size === selectable.length
                    ? new Set()
                    : new Set(selectable.map((row) => row.student.userId as string))
                )
              }
            >
              {studentIds.size === selectable.length && selectable.length > 0
                ? t["common.clearAll"]
                : t["common.selectAll"]}
            </AppButton>
          </div>
        </div>

        {legend.length > 0 && (
          <ul className="caption flex flex-wrap gap-x-4 gap-y-1 text-app-muted">
            {legend.map((dot) => (
              <li key={dot} className="flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${ROW_DOTS[dot].dot}`} />
                <span>{t[ROW_DOTS[dot].meaningKey]}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {rosterLoading &&
            students.length === 0 &&
            SKELETON_KEYS.map((key) => <Skeleton key={key} className="h-7" />)}
          {rosterError && students.length === 0 && (
            <p className="body-sm col-span-full text-app-overdue">{t["roster.loadError"]}</p>
          )}
          {rows.map(({ student, state }) => (
            <label
              key={student.myBestUserId}
              className={`body-sm group flex items-center gap-2 rounded-lg p-1.5 ${rowClassName(
                state
              )}`}
              title={state.hint}
            >
              {/* The real control, kept for the keyboard and screen readers; the circle is its face. */}
              <input
                type="checkbox"
                className="peer sr-only"
                disabled={state.disabled}
                checked={student.userId ? studentIds.has(student.userId) : false}
                onChange={() => student.userId && toggleStudent(student.userId)}
              />
              <CheckCircle
                checked={student.userId ? studentIds.has(student.userId) : false}
                size="sm"
                className="peer-focus-visible:ring-2 peer-focus-visible:ring-app-ink peer-focus-visible:ring-offset-2"
              />
              <span className="truncate">{student.name}</span>
              {state.dot && (
                <>
                  <span
                    className={`ml-auto h-2.5 w-2.5 shrink-0 rounded-full ${ROW_DOTS[state.dot].dot}`}
                  />
                  <span className="sr-only">{t[ROW_DOTS[state.dot].meaningKey]}</span>
                </>
              )}
            </label>
          ))}
        </div>
      </section>

      <section className="app-card space-y-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
          <span className="app-title">{t["assign.slides"]}</span>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <StatPill tone={slideCount > 0 ? "aqua" : "muted"}>
              {lessonCount === 0
                ? format(t["assign.slideCount"], { count: slideCount })
                : format(
                    lessonCount === 1 ? t["assign.fromLessonOne"] : t["assign.fromLessonOther"],
                    { count: slideCount, lessons: lessonCount }
                  )}
            </StatPill>
            {selectedSlides.size > 0 && (
              <AppButton tone="quiet" size="sm" onClick={() => updateSelectedSlides(new Map())}>
                {t["common.clearAll"]}
              </AppButton>
            )}
            <AppButton
              size="sm"
              disabled={studentIds.size === 0}
              onClick={() => setIsPickingSlides(true)}
            >
              {t["assign.chooseSlides"]}
            </AppButton>
          </div>
        </div>

        {studentIds.size === 0 && selectedSlides.size === 0 ? (
          <p className="body-sm text-app-muted">{t["assign.pickStudentsFirst"]}</p>
        ) : selectedSlides.size === 0 ? (
          <p className="body-sm text-app-muted">{t["assign.noSlides"]}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {[...selectedSlides.entries()].map(([slideId, slide]) => (
              <li key={slideId} className="flex items-center gap-3 py-2">
                <span
                  className={`block w-12 shrink-0 overflow-hidden rounded-lg bg-app-sky-soft ${CANVAS_SIZE.freeform.aspectRatioClass}`}
                >
                  <SlideThumbnailBody slide={slide.slide} width={48} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="app-value block truncate">{slide.slide.name}</span>
                  {slide.lessonName && (
                    <span className="caption block truncate text-app-muted">
                      {slide.lessonName}
                    </span>
                  )}
                </span>
                {slide.slideIds.length > 1 && (
                  <span className="app-label shrink-0">
                    {format(t["assign.subslideCount"], { count: slide.slideIds.length - 1 })}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={format(t["assign.removeSlide"], { name: slide.slide.name })}
                  onClick={() => {
                    const next = new Map(selectedSlides);
                    next.delete(slideId);
                    updateSelectedSlides(next);
                  }}
                  className="shrink-0 text-gray-400 hover:text-app-overdue"
                >
                  <HiX className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {isPickingSlides && (
        <SlidePickerDialog
          initialSelection={selectedSlides}
          reach={reachQuery.data?.assignableReach}
          reachLoading={reachQuery.loading}
          onCancel={() => setIsPickingSlides(false)}
          onDone={(selection) => {
            updateSelectedSlides(selection);
            setIsPickingSlides(false);
          }}
        />
      )}
    </AppDialog>
  );
}
